import { useEffect, useRef } from "react";
import { motion, useMotionValue } from "framer-motion";
import Image from "next/image";
import { CheckCircle2, ExternalLink } from "lucide-react";
import { Rocket, Building2, Clock, Zap } from "lucide-react";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";

import { Navbar } from "@/components";
import Footer from "@/components/sections/Footer";
import SEO from "@/components/SEO";
import HubSpotMeetings from "@/components/HubSpotMeetings";
import { GeoRedirect } from "@/components/GeoRedirect";
import { getMeetingUrl } from "@/components/Cta";
import { BOOK_DEMO_EVENTS, pushToDataLayer } from "@/services/gtm";
import { PARTNERS } from "@/resources/constants";
import crossLinesPattern from "@/assets/svg/crosslines.svg";

export async function getStaticProps({ locale }: any) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["common", "home", "bookdemo"])),
    },
  };
}

// Reuses the differentiators already maintained for the homepage.
const RECAP_KEYS = ["hardware_agnostic", "real_time", "scalable", "customizable"];

const metricIcons = [
  { icon: Rocket, color: "electric-blue" },
  { icon: Building2, color: "transport-mint" },
  { icon: Clock, color: "retail-coral" },
  { icon: Zap, color: "industry-lilac" },
];

export default function BookADemo() {
  const { t } = useTranslation(["bookdemo", "home"]);
  const router = useRouter();
  const meetingUrl = getMeetingUrl(router.locale);

  // Partner carousel, matching the homepage treatment.
  const itemWidth = 140;
  const allPartners = [...PARTNERS, ...PARTNERS];
  const totalItemsCount = allPartners.length;
  const carouselRef = useRef(null);
  const x = useMotionValue(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      let newX = x.get() - 1;
      if (newX < (-totalItemsCount * itemWidth) / 2) {
        newX = 0;
      }
      x.set(newX);
    }, 20);
    return () => clearInterval(intervalId);
  }, [x, totalItemsCount]);

  // Entry point of the booking funnel in GTM.
  useEffect(() => {
    pushToDataLayer({
      event: BOOK_DEMO_EVENTS.pageView,
      page_locale: router.locale,
      referrer: typeof document !== "undefined" ? document.referrer : undefined,
    });
  }, [router.locale]);

  const steps = t("hero.steps", { returnObjects: true }) as string[];

  return (
    <>
      <GeoRedirect />
      <SEO
        title={t("meta.title")}
        description={t("meta.description")}
        ogImage="/images/og-home.png"
      />

      <div className="min-h-screen bg-background">
        <Navbar />

        {/* ---------- Hero + Scheduler ---------- */}
        <section className="relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-24">
          {/* Ambient glow */}
          <div className="pointer-events-none absolute inset-0 -z-10 opacity-40">
            <div
              className="absolute -top-40 left-1/4 h-[36rem] w-[36rem] rounded-full blur-3xl"
              style={{ background: "radial-gradient(circle, hsl(var(--electric-blue) / 0.18), transparent 65%)" }}
            />
            <div
              className="absolute -bottom-40 right-0 h-[32rem] w-[32rem] rounded-full blur-3xl"
              style={{ background: "radial-gradient(circle, hsl(var(--transport-mint) / 0.12), transparent 65%)" }}
            />
          </div>

          <div className="container mx-auto px-6">
            <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
              {/* Left: pitch */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="lg:sticky lg:top-32"
              >
                <p className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-electric-blue">
                  {t("hero.eyebrow")}
                </p>

                <h1 className="text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                  {t("hero.title")}
                </h1>

                <p className="mt-6 text-lg leading-relaxed text-secondary-foreground md:text-xl">
                  {t("hero.subtitle")}
                </p>

                <p className="mt-10 text-base font-semibold text-primary-foreground">
                  {t("hero.stepsIntro")}
                </p>

                <ol className="mt-6 space-y-5">
                  {steps.map((step, index) => (
                    <motion.li
                      key={index}
                      className="flex gap-4"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                    >
                      <span
                        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border text-sm font-bold"
                        style={{
                          borderColor: "hsl(var(--electric-blue) / 0.4)",
                          backgroundColor: "hsl(var(--electric-blue) / 0.12)",
                          color: "hsl(var(--electric-blue))",
                        }}
                      >
                        {index + 1}
                      </span>
                      <span className="pt-1 leading-relaxed text-secondary-foreground">
                        {step}
                      </span>
                    </motion.li>
                  ))}
                </ol>
              </motion.div>

              {/* Right: HubSpot scheduler */}
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                className="relative"
              >

                {/*
                  No background here on purpose: HubSpot sets its own <body> to
                  `transparent` while embedded, so the scheduler sits straight
                  on the page and any surface we add shows up as a white frame.
                */}
                <HubSpotMeetings url={meetingUrl} />

                <p className="mt-4 text-center text-sm text-secondary-foreground">
                  {t("scheduler.fallback")}{" "}
                  <a
                    href={meetingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-semibold text-electric-blue hover:underline"
                  >
                    {t("scheduler.fallbackLink")}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ---------- Trusted by ---------- */}
        <motion.section
          className="pb-20 md:pb-24"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="container mx-auto px-6 text-center">
            <p className="mb-8 text-sm font-semibold text-secondary-foreground">
              {t("partners")}
            </p>

            <div className="mx-auto flex max-w-6xl overflow-hidden">
              <motion.div className="flex gap-4" ref={carouselRef} style={{ x }}>
                {allPartners.map((partner, index) => (
                  <div
                    key={index}
                    className="flex h-20 min-w-[140px] items-center justify-center rounded-lg border border-border bg-container-background px-4 py-2 transition-opacity hover:opacity-80 md:min-w-[160px]"
                  >
                    <Image
                      height={80}
                      width={160}
                      className="h-12 w-auto object-contain opacity-60 grayscale md:h-16 lg:h-20"
                      src={partner}
                      alt="Partner logo"
                    />
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* ---------- Solution recap ---------- */}
        <motion.section
          className="border-t border-border py-20 md:py-24"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          viewport={{ once: true, margin: "-50px" }}
        >
          <div className="container mx-auto px-6">
            <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
              <div className="lg:sticky lg:top-32 lg:self-start">
                <p className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-electric-blue">
                  {t("recap.eyebrow")}
                </p>
                <h2 className="text-3xl font-bold md:text-4xl lg:text-5xl">
                  {t("recap.title")}
                </h2>
                <p className="mt-6 max-w-md leading-relaxed text-secondary-foreground">
                  {t("recap.subtitle")}
                </p>
              </div>

              <ul className="relative space-y-10 border-l border-border pl-8">
                {RECAP_KEYS.map((key, index) => (
                  <motion.li
                    key={key}
                    className="relative"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <span className="absolute -left-[2.65rem] flex h-7 w-7 items-center justify-center rounded-md bg-background">
                      <CheckCircle2
                        className="h-6 w-6"
                        style={{ color: "hsl(var(--electric-blue))" }}
                      />
                    </span>
                    <h3 className="mb-2 text-lg font-bold text-primary-foreground">
                      {t(`home:benefits.items.${key}.title`)}
                    </h3>
                    <p className="leading-relaxed text-secondary-foreground">
                      {t(`home:benefits.items.${key}.description`)}
                    </p>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </motion.section>

        {/* ---------- Results ---------- */}
        <motion.section
          className="border-t border-border py-20 md:py-24"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          viewport={{ once: true, margin: "-50px" }}
        >
          <div className="container mx-auto px-6">
            <div className="mb-12 md:mb-16">
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-electric-blue">
                {t("results.eyebrow")}
              </p>
              <h2 className="max-w-2xl text-3xl font-bold md:text-4xl lg:text-5xl">
                {t("home:transformationProof.title")}
              </h2>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {metricIcons.map((metric, index) => (
                <motion.div
                  key={index}
                  className="light_effect_card relative overflow-hidden rounded-xl border border-border bg-container-background p-6"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div
                    className="pointer-events-none absolute inset-0 rounded-xl"
                    style={{
                      backgroundImage: `url(${crossLinesPattern.src})`,
                      backgroundPosition: "center",
                      backgroundRepeat: "repeat",
                      backgroundSize: "60px",
                      opacity: 0.15,
                      maskImage:
                        "radial-gradient(ellipse 150% 150% at top right, rgba(0,0,0,1) 0%, rgba(0,0,0,0.3) 25%, rgba(0,0,0,0) 50%)",
                      WebkitMaskImage:
                        "radial-gradient(ellipse 150% 150% at top right, rgba(0,0,0,1) 0%, rgba(0,0,0,0.3) 25%, rgba(0,0,0,0) 50%)",
                    }}
                  />

                  <div className="relative z-10">
                    <div
                      className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg"
                      style={{ backgroundColor: `hsl(var(--${metric.color}) / 0.15)` }}
                    >
                      <metric.icon
                        className="h-6 w-6"
                        style={{ color: `hsl(var(--${metric.color}))` }}
                      />
                    </div>
                    <div
                      className="mb-2 text-2xl font-bold md:text-3xl"
                      style={{ color: `hsl(var(--${metric.color}))` }}
                    >
                      {t(`home:transformationProof.metrics.${index}.value`)}
                    </div>
                    <p className="mb-2 text-sm font-semibold text-primary-foreground">
                      {t(`home:transformationProof.metrics.${index}.label`)}
                    </p>
                    <p className="text-xs leading-relaxed text-secondary-foreground">
                      {t(`home:transformationProof.metrics.${index}.description`)}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        <Footer />
      </div>
    </>
  );
}
